<template>
  <base-modal ref="modal" size="4xl" :loading="isLoading" @close="hide">
    <base-card shadow class="tw-bg-white tw-rounded-md">
      <div>
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
          <h2 class="tw-text-xl tw-font-semibold tw-text-gray-900">
            Notifications
          </h2>
        </div>

        <div v-if="isLoading" class="tw-space-y-3">
          <div v-for="i in 3" :key="`loading-${i}`" class="tw-bg-gray-50 tw-rounded-md tw-p-3 tw-mb-2">
            <base-skeleton-loader class="tw-h-3 tw-w-48 tw-mb-1" />
            <base-skeleton-loader class="tw-h-2 tw-w-full tw-mb-1" />
            <base-skeleton-loader class="tw-h-2 tw-w-32" />
          </div>
        </div>

        <div v-else-if="notifications.length > 0" class="tw-space-y-3">
          <div v-for="notification in notifications" :key="notification.businessNotificationId" class="tw-bg-gray-50 tw-rounded-md tw-p-3 tw-relative tw-mb-2">
            <div class="tw-mb-2">
              <div>
                <div class="tw-text-sm tw-text-gray-900 tw-break-words tw-whitespace-normal" v-html="convertNewLinesToBreaks(notification.message)"></div>
                <div class="tw-text-xs tw-text-right tw-font-medium tw-text-gray-400 tw-mt-2">
                  Sent at {{ formatDate(notification.insertedAt) }} by {{ notification.insertedByFullName }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="tw-py-5 tw-text-center tw-text-gray-600">
          No new notifications.
        </div>

        <div v-if="notifications.length > 0" class="tw-mt-4 tw-flex tw-items-center tw-justify-between">
          <div class="tw-text-sm tw-text-gray-500">Showing {{ page.skip + 1 }} to {{ Math.min(page.skip + page.take, page.total) }} of {{ page.total }} notifications</div>
          <div class="tw-space-x-2">
            <base-button
              type="secondary"
              :disabled="page.skip === 0"
              @click="previous"
              class="tw-rounded-md tw-shadow-sm tw-text-gray-600 hover:tw-bg-gray-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2"
            >
              Previous
            </base-button>
            <base-button
              type="secondary"
              :disabled="page.skip + page.take >= page.total"
              @click="next"
              class="tw-rounded-md tw-shadow-sm tw-text-gray-600 hover:tw-bg-gray-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2"
            >
              Next
            </base-button>
            <base-button
              v-if="isLocationFiltered && notifications.some((n) => !n.isRead)"
              type="primary"
              class="tw-whitespace-nowrap tw-rounded-md tw-shadow-sm tw-bg-blue-600 tw-text-white tw-font-medium"
              :disabled="isMarkingAll"
              @click="markAsRead(null, true)"
            >
              <span v-if="!isMarkingAll">Mark All as Read</span>
              <span v-else class="tw-flex tw-items-center">
                Marking...
              </span>
            </base-button>
          </div>
        </div>
      </div>
    </base-card>
  </base-modal>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import axios from "axios";

const TAKE_KEY = "predictile_business_notifications_take";

export default {
  name: "ProviderNotificationsModal",
  props: {
    businessId: String,
    initialItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isFormValid: false,
      isSubmitting: false,
      isLoading: false,
      isMarkingAll: false,
      notificationLoading: {},
      notification: {
        message: "",
      },
      notifications: this.initialItems,
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 5, // Adjusted default take
        skip: 0,
        total: 0,
      },
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("location", ["locations"]),
    ...mapGetters("role", ["isPredictile", "isBusinessManagement", "isBusinessAdministrator", "isLocationFiltered"]),
  },
  created() {
    this.EventBus.$on("NOTIFICATION_Send", this.loadPage);
    if (!this.initialItems.length) {
      this.loadPage(); // fallback if no props passed
    }
  },
  methods: {
    formatDate(date) {
      return moment
        .utc(date)
        .local()
        .format("M/D/YY h:mm A");
    },

    next() {
      this.page.skip += this.page.take;
      this.loadPage();
    },
    previous() {
      this.page.skip -= this.page.take;
      this.loadPage();
    },

    loadPage() {
      this.isLoading = true;

      let url = `/businesses/${this.businessId}/provider-notifications/unread?skip=${this.page.skip}&take=${this.page.take}`;
      url += `&locationFilter=${this.isLocationFiltered ? "true" : "false"}`;
      this.$axios
        .get(url)
        .then((res) => {
          this.notifications = res.data.items;
          this.page.take = res.data.take;
          this.page.skip = res.data.skip;
          this.page.total = res.data.total;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load notifications",
            message: "We had a problem retrieving the list of notifications. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    truncateToWords(text, wordLimit = 50) {
      if (!text || typeof text !== "string") return "";
      const words = text.trim().split(/\s+/);
      if (words.length <= wordLimit) {
        return words.join(" ");
      }
      return words.slice(0, wordLimit).join(" ") + "...";
    },

    async markAsRead(id, markAll = false) {
      if (markAll) {
        this.isMarkingAll = true;
      } else {
        this.$set(this.notificationLoading, id, true);
      }

      try {
        const endpointId = id !== null && id !== undefined ? id : "all";
        await axios.post(`/businesses/${this.businessId}/notification/read/${endpointId}`, {
          markAll,
        });
        this.hide();
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Mark as read",
          message: markAll ? "All notifications have been marked as read." : "The notification has been marked as read successfully.",
        });

        if (markAll) {
          this.notifications.forEach((n) => (n.isRead = true));
        } else {
          const index = this.notifications.findIndex((n) => n.businessNotificationId === id);
          if (index !== -1) {
            this.notifications[index].isRead = true;
          }
        }
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to mark as read",
          message: markAll ? "Could not mark all notifications as read. Please try again." : "We were not able to mark the notification as read. Please try again.",
          error,
        });
      } finally {
        if (markAll) {
          this.isMarkingAll = false;
        } else {
          this.$delete(this.notificationLoading, id);
        }
      }
    },
    show() {
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    convertNewLinesToBreaks(text) {
      if (!text) return "";
      return text.replace(/\n/g, "<br />");
    },
  },
};
</script>
